import apiClient from './client'
import type {
  ClusterInfo,
  ClusterStats,
  Service,
  Node,
  Task,
  TraceSummary,
  TraceDetail,
  ServiceDependency,
  Metrics,
  AssistantResponse,
  Volume,
  VolumeListResponse,
  CreateVolumeRequest,
  Secret,
  CreateSecretRequest,
  SecretUsage,
  Network,
  CreateNetworkRequest,
} from '@/types'

// Cluster API
export const clusterApi = {
  getInfo: () => apiClient.get<ClusterInfo>('/cluster'),
  getStats: () => apiClient.get<ClusterStats>('/cluster/stats'),
}

// Services API
export const servicesApi = {
  list: (filters?: Record<string, any>) =>
    apiClient.get<Service[]>('/services', { params: filters }),
  
  get: (id: string) =>
    apiClient.get<Service>(`/services/${id}`),
  
  create: (service: Partial<Service['Spec']>) =>
    apiClient.post<{ ID: string }>('/services', service),
  
  update: (id: string, spec: Partial<Service['Spec']>) =>
    apiClient.put(`/services/${id}`, spec),
  
  delete: (id: string) =>
    apiClient.delete(`/services/${id}`),
  
  scale: (id: string, replicas: number) =>
    apiClient.post(`/services/${id}/scale`, { replicas }),
  
  getTasks: (serviceId: string) =>
    apiClient.get<Task[]>(`/services/${serviceId}/tasks`),
}

// Nodes API
export const nodesApi = {
  list: () =>
    apiClient.get<Node[]>('/nodes'),
  
  get: (id: string) =>
    apiClient.get<Node>(`/nodes/${id}`),
  
  update: (id: string, spec: Partial<Node['Spec']>) =>
    apiClient.put(`/nodes/${id}`, spec),
  
  drain: (id: string) =>
    apiClient.post(`/nodes/${id}/drain`),
}

// Metrics API
export const metricsApi = {
  getClusterMetrics: (timeRange?: { start: string; end: string }) =>
    apiClient.get<Metrics[]>('/metrics/cluster', { params: timeRange }),
  
  getServiceMetrics: (id: string, timeRange?: { start: string; end: string }) =>
    apiClient.get<Metrics[]>(`/metrics/services/${id}`, { params: timeRange }),
  
  getNodeMetrics: (id: string, timeRange?: { start: string; end: string }) =>
    apiClient.get<Metrics[]>(`/metrics/nodes/${id}`, { params: timeRange }),
}

// Traces API
export const tracesApi = {
  list: (params?: {
    service?: string
    operation?: string
    errors_only?: boolean
    min_duration?: number
    max_duration?: number
    limit?: number
  }) =>
    apiClient.get<TraceSummary[]>('/traces', { params }),

  get: (id: string) =>
    apiClient.get<TraceDetail>(`/traces/${id}`),

  getDependencies: (timeRange?: { start: string; end: string }) =>
    apiClient.get<ServiceDependency[]>('/traces/service-map', { params: timeRange }),
}

// Assistant API
export const assistantApi = {
  query: (prompt: string, context?: any) =>
    apiClient.post<AssistantResponse>('/assistant/query', { prompt, context }),
}

// Events API (SSE)
export const eventsApi = {
  stream: () => {
    const eventSource = new EventSource('/api/events')
    return eventSource
  },
}

// Volumes API
export const volumesApi = {
  list: (filters?: {
    driver?: string
    label?: string
    name?: string
    dangling?: boolean
  }) =>
    apiClient.get<VolumeListResponse>('/volumes', { params: filters }),

  get: (name: string) =>
    apiClient.get<Volume>(`/volumes/${name}`),

  create: (volume: CreateVolumeRequest) =>
    apiClient.post<Volume>('/volumes', volume),

  delete: (name: string) =>
    apiClient.delete(`/volumes/${name}`),
}

// Secrets API
export const secretsApi = {
  list: (filters?: Record<string, any>) =>
    apiClient.get<Secret[]>('/v1.44/secrets', { params: filters }),

  get: (id: string) =>
    apiClient.get<Secret>(`/v1.44/secrets/${id}`),

  create: (secret: CreateSecretRequest) =>
    apiClient.post<Secret>('/v1.44/secrets', secret),

  update: (id: string, secret: CreateSecretRequest) =>
    apiClient.post<Secret>(`/v1.44/secrets/${id}/update`, secret),

  delete: (id: string) =>
    apiClient.delete(`/v1.44/secrets/${id}`),

  // Get services using this secret (mock for now)
  getUsage: (id: string) =>
    apiClient.get<SecretUsage[]>(`/v1.44/secrets/${id}/usage`),
}

// Networks API
export const networksApi = {
  list: (filters?: { driver?: string; name?: string }) =>
    apiClient.get<Network[]>('/v1/networks', { params: filters }),

  get: (id: string) =>
    apiClient.get<Network>(`/v1/networks/${id}`),

  create: (network: CreateNetworkRequest) =>
    apiClient.post<{ Id: string; Warning: string }>('/v1/networks', network),

  delete: (id: string) =>
    apiClient.delete(`/v1/networks/${id}`),

  connect: (id: string, container: string) =>
    apiClient.post(`/v1/networks/${id}/connect`, { Container: container }),

  disconnect: (id: string, container: string, force?: boolean) =>
    apiClient.post(`/v1/networks/${id}/disconnect`, { Container: container, Force: force }),
}
// GPU API
export const gpuApi = {
  getNodeGpus: (nodeId: string) =>
    apiClient.get<import('@/types').NodeGpuResponse>(`/nodes/${nodeId}/gpus`),

  getAllocations: () =>
    apiClient.get<import('@/types').GpuAllocationsResponse>('/gpus/allocations'),
}
